import { Formiz } from "@formiz/core";
import { UseFormValues } from "@formiz/core/dist/types/form.types";
import { isMaxLength, isRequired } from "@formiz/validations";
import { useTranslation } from "@next-platform/client-common-core";
import { Spinner } from "@next-platform/client-common-spicy-ui";
import { Lorebook, Rulebook, UserExpectedResolution, UserIssueType } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo } from "react";

import CharacterCard from "@/components/Character/CharacterCardV1";
import FormField from "@/components/common/FormField";
import RadioGroupField from "@/components/common/RadioGroupField";
import { SpicyInput, SpicyTextarea } from "@/components/customComponents";
import LorebookCard from "@/pages/LorebookMy/LorebookCard";
import RulebookCard from "@/pages/RulebookMy/RulebookCard";

const REPORT_DETAILS_MAX_LENGTH = 250;

const RADIO_GROUP_CLASS_NAMES = { wrapper: "gap-[20px]" };
const emptyCharacterCardActionsState = {};

export enum ItemType {
  Character = "character",
  Lorebook = "lorebook",
  Rulebook = "rulebook",
}

interface IReportFormProps {
  reportForm: UseFormValues;
  initialValues: Record<string, unknown>;
  submitForm: () => void;
  itemId: string;
  itemType?: ItemType;
  /** The report DTO decides whether an expected-resolution pick exists — rulebook reports have no such field. */
  isResolutionVisible?: boolean;
  itemData: unknown;
  isLoading?: boolean;
  isSubmitAttempted?: boolean;
  setStateIsSubmitAttempted?: React.Dispatch<React.SetStateAction<boolean>>;
}

type Translate = (key: string) => string;

type ReportCopy = {
  guidelinesLabel: string;
  categorizationLabel: string;
  incorrectAuthorizationLabel: string;
  invalidLabel: string;
  privateLabel: string;
  modifiedLabel: string;
  deletedLabel: string;
  idRequired: string;
  idTitle: string;
  idDescription: string;
  issueRequired: string;
  resolutionRequired: string;
  detailsMaxLength: string;
  detailsRequired: string;
  typeOfIssueTitle: string;
  typeOfIssueDescription: string;
  detailsTitle: string;
  detailsDescription: string;
  expectedResolutionTitle: string;
  expectedResolutionDescription: string;
};

/** All user-facing copy, keyed per report item type — every `t` call keeps a literal key. */
const reportCopyFor = (itemType: ItemType, translate: Translate): ReportCopy => {
  if (itemType === ItemType.Rulebook) {
    return {
      guidelinesLabel: translate("rulebook:reportPage.issue.guidelines.label"),
      categorizationLabel: translate("rulebook:reportPage.issue.categorization.label"),
      incorrectAuthorizationLabel: translate("rulebook:reportPage.issue.incorrectAuthorization.label"),
      invalidLabel: translate("rulebook:reportPage.issue.invalid.label"),
      privateLabel: translate("rulebook:reportPage.resolution.private.label"),
      modifiedLabel: translate("rulebook:reportPage.resolution.modified.label"),
      deletedLabel: translate("rulebook:reportPage.resolution.deleted.label"),
      idRequired: translate("rulebook:reportPage.form.error.rulebookIdRequired"),
      idTitle: translate("rulebook:reportPage.rulebookId.title"),
      idDescription: translate("rulebook:reportPage.rulebookId.description"),
      issueRequired: translate("rulebook:reportPage.form.error.fillAllFields"),
      resolutionRequired: translate("rulebook:reportPage.form.error.fillAllFields"),
      detailsMaxLength: translate("rulebook:reportPage.form.error.detailsMaxLength"),
      detailsRequired: translate("rulebook:reportPage.form.error.detailsRequired"),
      typeOfIssueTitle: translate("rulebook:reportPage.typeOfIssue.title"),
      typeOfIssueDescription: translate("rulebook:reportPage.typeOfIssue.description"),
      detailsTitle: translate("rulebook:reportPage.details.title"),
      detailsDescription: translate("rulebook:reportPage.details.description"),
      expectedResolutionTitle: translate("rulebook:reportPage.expectedResolution.title"),
      expectedResolutionDescription: translate("rulebook:reportPage.expectedResolution.description"),
    };
  }
  if (itemType === ItemType.Character) {
    return {
      guidelinesLabel: translate("chatbot:reportPage.issue.guidelines.label"),
      categorizationLabel: translate("chatbot:reportPage.issue.categorization.label"),
      incorrectAuthorizationLabel: translate("chatbot:reportPage.issue.incorrectAuthorization.label"),
      invalidLabel: translate("chatbot:reportPage.issue.invalid.label"),
      privateLabel: translate("chatbot:reportPage.resolution.private.label"),
      modifiedLabel: translate("chatbot:reportPage.resolution.modified.label"),
      deletedLabel: translate("chatbot:reportPage.resolution.deleted.label"),
      idRequired: translate("chatbot:reportPage.form.error.characterIdRequired"),
      idTitle: translate("chatbot:reportPage.chatbotId.title"),
      idDescription: translate("chatbot:reportPage.chatbotId.description"),
      issueRequired: translate("chatbot:reportPage.form.error.issueRequired"),
      resolutionRequired: translate("chatbot:reportPage.form.error.resolutionRequired"),
      detailsMaxLength: translate("chatbot:reportPage.form.error.detailsMaxLength"),
      detailsRequired: translate("chatbot:reportPage.form.error.detailsRequired"),
      typeOfIssueTitle: translate("chatbot:reportPage.typeOfIssue.title"),
      typeOfIssueDescription: translate("chatbot:reportPage.typeOfIssue.description"),
      detailsTitle: translate("chatbot:reportPage.details.title"),
      detailsDescription: translate("chatbot:reportPage.details.description"),
      expectedResolutionTitle: translate("chatbot:reportPage.expectedResolution.title"),
      expectedResolutionDescription: translate("chatbot:reportPage.expectedResolution.description"),
    };
  }
  // The lorebook namespace has no per-field issue/resolution copy, so it reuses the same
  // `fillAllFields` string the native lorebook report form already uses for these two fields.
  return {
    guidelinesLabel: translate("lorebook:reportPage.issue.guidelines.label"),
    categorizationLabel: translate("lorebook:reportPage.issue.categorization.label"),
    incorrectAuthorizationLabel: translate("lorebook:reportPage.issue.incorrectAuthorization.label"),
    invalidLabel: translate("lorebook:reportPage.issue.invalid.label"),
    privateLabel: translate("lorebook:reportPage.resolution.private.label"),
    modifiedLabel: translate("lorebook:reportPage.resolution.modified.label"),
    deletedLabel: translate("lorebook:reportPage.resolution.deleted.label"),
    idRequired: translate("lorebook:reportPage.form.error.lorebookIdRequired"),
    idTitle: translate("lorebook:reportPage.chatbotId.title"),
    idDescription: translate("lorebook:reportPage.chatbotId.description"),
    issueRequired: translate("lorebook:reportPage.form.error.fillAllFields"),
    resolutionRequired: translate("lorebook:reportPage.form.error.fillAllFields"),
    detailsMaxLength: translate("lorebook:reportPage.form.error.detailsMaxLength"),
    detailsRequired: translate("lorebook:reportPage.form.error.detailsRequired"),
    typeOfIssueTitle: translate("lorebook:reportPage.typeOfIssue.title"),
    typeOfIssueDescription: translate("lorebook:reportPage.typeOfIssue.description"),
    detailsTitle: translate("lorebook:reportPage.details.title"),
    detailsDescription: translate("lorebook:reportPage.details.description"),
    expectedResolutionTitle: translate("lorebook:reportPage.expectedResolution.title"),
    expectedResolutionDescription: translate("lorebook:reportPage.expectedResolution.description"),
  };
};

const buildReportFormConfiguration = ({ copy, itemType }: { copy: ReportCopy; itemType: ItemType }) => {
  const userIssueTypeOptions = [
    { value: UserIssueType.Guidelines, label: copy.guidelinesLabel },
    { value: UserIssueType.Categorization, label: copy.categorizationLabel },
    { value: UserIssueType.IncorrectAuthorization, label: copy.incorrectAuthorizationLabel },
    { value: UserIssueType.Invalid, label: copy.invalidLabel },
  ];
  const userExpectedResolutionOptions = [
    { value: UserExpectedResolution.Private, label: copy.privateLabel },
    { value: UserExpectedResolution.Modified, label: copy.modifiedLabel },
    { value: UserExpectedResolution.Deleted, label: copy.deletedLabel },
  ];
  const chatbotIdValidations = [{ rule: isRequired(), message: copy.idRequired }];
  const issueTypeValidations = [{ rule: isRequired(), message: copy.issueRequired }];
  const expectedResolutionValidations = [{ rule: isRequired(), message: copy.resolutionRequired }];
  // Rulebook reports accept a reason alone; characters and lorebooks still require details.
  const detailsValidations = [
    {
      rule: isMaxLength(REPORT_DETAILS_MAX_LENGTH),
      message: copy.detailsMaxLength,
    },
    ...(itemType === ItemType.Rulebook ? [] : [{ rule: isRequired(), message: copy.detailsRequired }]),
  ];

  return {
    chatbotIdValidations,
    detailsValidations,
    expectedResolutionValidations,
    issueTypeValidations,
    userExpectedResolutionOptions,
    userIssueTypeOptions,
  };
};

const renderReportItemPreview = ({ itemData, itemType }: { itemData: unknown; itemType: ItemType }) => {
  if (!itemData) {
    return null;
  }
  if (itemType === ItemType.Character) {
    // @ts-expect-error CharacterCard is a legacy component with many conditional rendering paths; full refactor is tracked separately
    return <CharacterCard character={itemData as Record<string, unknown>} hideOptions={true} sortBy="" />;
  }
  if (itemType === ItemType.Lorebook) {
    return <LorebookCard lorebook={itemData as unknown as Lorebook} characterCardActionsState={emptyCharacterCardActionsState} />;
  }
  if (itemType === ItemType.Rulebook) {
    return <RulebookCard rulebook={itemData as unknown as Rulebook} characterCardActionsState={emptyCharacterCardActionsState} isPreview />;
  }

  return null;
};

const ReportForm = ({
  reportForm,
  initialValues,
  submitForm,
  itemId,
  itemType = ItemType.Character,
  isResolutionVisible = true,
  itemData,
  isLoading = false,
  isSubmitAttempted = false,
  setStateIsSubmitAttempted,
}: IReportFormProps) => {
  const { t } = useTranslation();
  const copy = useMemo(() => reportCopyFor(itemType, t), [itemType, t]);
  const {
    chatbotIdValidations,
    detailsValidations,
    expectedResolutionValidations,
    issueTypeValidations,
    userExpectedResolutionOptions,
    userIssueTypeOptions,
  } = useMemo(() => buildReportFormConfiguration({ copy, itemType }), [copy, itemType]);
  const handleInvalidSubmit = useCallback(() => setStateIsSubmitAttempted?.(true), [setStateIsSubmitAttempted]);
  const itemPreview = renderReportItemPreview({ itemData, itemType });

  useEffect(() => {
    if (reportForm.isValid) {
      setStateIsSubmitAttempted?.(false);
    }
  }, [reportForm.isValid, setStateIsSubmitAttempted]);

  return (
    <Formiz connect={reportForm} initialValues={initialValues} onValidSubmit={submitForm} onInvalidSubmit={handleInvalidSubmit}>
      <form onSubmit={reportForm.submit} className="flex flex-col gap-sm">
        {itemPreview}
        {isLoading ? <Spinner /> : null}
        {!itemId && (
          <>
            <FormField
              component={SpicyInput}
              variant="primary"
              color="primary"
              name={`${itemType}_id`}
              title={copy.idTitle}
              desc={copy.idDescription}
              placeholder="d3653a1d-a4ac-4e4c-8892-804fea5e4368"
              maxLength="50"
              aria-labelledby={`${itemType}Idd`}
              validations={chatbotIdValidations}
              showError={isSubmitAttempted}
            />
          </>
        )}
        <RadioGroupField
          name="userIssueType"
          validations={issueTypeValidations}
          showError={isSubmitAttempted}
          options={userIssueTypeOptions}
          title={copy.typeOfIssueTitle}
          desc={copy.typeOfIssueDescription}
          orientation="vertical"
          size="sm"
          aria-labelledby="issue"
          classNames={RADIO_GROUP_CLASS_NAMES}
        />
        <FormField
          component={SpicyTextarea}
          variant="primary"
          color="primary"
          name={"userReportDetails"}
          title={copy.detailsTitle}
          desc={copy.detailsDescription}
          minRows={3}
          maxRows={3}
          maxLength={REPORT_DETAILS_MAX_LENGTH}
          aria-labelledby="details"
          validations={detailsValidations}
          showError={isSubmitAttempted}
        />
        {isResolutionVisible ? (
          <RadioGroupField
            name="userExpectedResolution"
            validations={expectedResolutionValidations}
            showError={isSubmitAttempted}
            options={userExpectedResolutionOptions}
            title={copy.expectedResolutionTitle}
            desc={copy.expectedResolutionDescription}
            orientation="vertical"
            size="sm"
            aria-labelledby="visibility"
            classNames={RADIO_GROUP_CLASS_NAMES}
          />
        ) : null}
      </form>
    </Formiz>
  );
};

export default ReportForm;
